import { skipToken, useSuspenseQuery } from "@apollo/client/react";
import { parseISO } from "date-fns";
import { ExternalLink } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { useDisplayCategoryName, useDisplayName } from "~/common/functions";
import { OrderBy, type RecordsBoolExp } from "~/common/types";
import { CopyIdsButton } from "~/components/copy-button";
import { Badge } from "~/components/ui";
import { GetEntitiesDocument, type GetEntitiesQuery, type GetEntitiesQueryVariables } from "~/features/entities/gqls";
import {
  GetRelationsDocument,
  type GetRelationsQuery,
  type GetRelationsQueryVariables,
} from "~/features/relations/gqls";
import { useTenant } from "~/features/tenant/context";
import { rootRoute as R } from "~/routes";
import { v2Route } from "~/v2/routes";

type RecordKind = "entity" | "relation" | "record_update";

type DipRecordsPeekProps = {
  batchChangeRequestsJobId?: string;
  recordComposerDefinitionId?: string;
  recordKind?: RecordKind;
};

type EntityRecord = GetEntitiesQuery["records"][number];
type RelationRecord = GetRelationsQuery["records"][number];
// A record tagged with its own kind so a mixed (record_update) list can render each row correctly.
type KindedRecord = { kind: "entity"; record: EntityRecord } | { kind: "relation"; record: RelationRecord };

const LIMIT = 200;

function buildGeneratedRecordsWhere(
  batchChangeRequestsJobId: string,
  recordComposerDefinitionId: string,
  kind: "entity" | "relation",
): RecordsBoolExp {
  return {
    _and: [
      kind === "entity" ? { entityRecord: {} } : { relationRecord: {} },
      {
        changeRequestItemRecords: {
          changeRequestItem: {
            changeRequest: {
              batchChangeRequestsJobId: { _eq: batchChangeRequestsJobId },
              statusId: { _eq: "committed" },
              changeRequestRecordComposerDefinition: {
                recordComposerDefinitionId: { _eq: recordComposerDefinitionId },
              },
            },
          },
        },
      },
    ],
  };
}

function RecordCategories({ record }: { record: EntityRecord | RelationRecord }) {
  const getCategoryName = useDisplayCategoryName();
  const categories = record.recordMetadata.recordMetadataCategories;
  if (categories.length === 0) return <span className="text-fg-3">-</span>;

  return (
    <div className="flex flex-wrap gap-1">
      {categories.map((rc) => (
        <Badge key={rc.category.id} tone="default">
          {getCategoryName(rc.category)}
        </Badge>
      ))}
    </div>
  );
}

function recordPath(record: EntityRecord | RelationRecord, kind: "entity" | "relation") {
  if (kind === "entity") {
    return v2Route.entities.$buildPath({ searchParams: { selectedEntity: record.id } });
  }

  // V2 relations is still a placeholder in this migration slice, so keep the canonical detail fallback.
  return R.relations.detail.$buildPath({ params: { id: record.id } });
}

export function DipRecordsPeek({
  batchChangeRequestsJobId,
  recordComposerDefinitionId,
  recordKind = "entity",
}: DipRecordsPeekProps) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const getDisplayName = useDisplayName();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }

  // record_update composers produce both entity and relation records, so both kinds are fetched
  // and merged; entity/relation composers fetch only their own kind.
  const wantEntity = recordKind === "entity" || recordKind === "record_update";
  const wantRelation = recordKind === "relation" || recordKind === "record_update";
  const hasTarget = Boolean(batchChangeRequestsJobId && recordComposerDefinitionId);

  const entityVariables = useMemo(
    () =>
      wantEntity && batchChangeRequestsJobId && recordComposerDefinitionId
        ? {
            tenantId,
            limit: LIMIT,
            offset: 0,
            where: buildGeneratedRecordsWhere(batchChangeRequestsJobId, recordComposerDefinitionId, "entity"),
            order_by: { updatedAt: OrderBy.Desc },
          }
        : null,
    [wantEntity, tenantId, batchChangeRequestsJobId, recordComposerDefinitionId],
  );
  const relationVariables = useMemo(
    () =>
      wantRelation && batchChangeRequestsJobId && recordComposerDefinitionId
        ? {
            tenantId,
            limit: LIMIT,
            offset: 0,
            where: buildGeneratedRecordsWhere(batchChangeRequestsJobId, recordComposerDefinitionId, "relation"),
            order_by: { updatedAt: OrderBy.Desc },
          }
        : null,
    [wantRelation, tenantId, batchChangeRequestsJobId, recordComposerDefinitionId],
  );

  // cache-and-network so the list revalidates on every open: the where clause filters on
  // committed change requests, so a peek opened before commit caches an empty result for these
  // variables. cache-first would then serve that stale empty list after the commit produces the
  // records; revalidating on mount surfaces the freshly committed records instead.
  const { data: entityData } = useSuspenseQuery<GetEntitiesQuery, GetEntitiesQueryVariables>(
    GetEntitiesDocument,
    entityVariables ? { variables: entityVariables, fetchPolicy: "cache-and-network" } : skipToken,
  );
  const { data: relationData } = useSuspenseQuery<GetRelationsQuery, GetRelationsQueryVariables>(
    GetRelationsDocument,
    relationVariables ? { variables: relationVariables, fetchPolicy: "cache-and-network" } : skipToken,
  );

  if (!hasTarget) {
    return <p className="rounded-[var(--radius-md)] border border-border-default p-4 text-12-regular text-fg-3">-</p>;
  }

  const rows: KindedRecord[] = [
    ...(entityData?.records ?? []).map((record): KindedRecord => ({ kind: "entity", record })),
    ...(relationData?.records ?? []).map((record): KindedRecord => ({ kind: "relation", record })),
  ];
  const totalCount =
    (entityData?.recordsAggregate.aggregate?.totalCount ?? 0) +
    (relationData?.recordsAggregate.aggregate?.totalCount ?? 0);
  // Show source/target columns whenever relation records may appear (relation or record_update peeks).
  const showRelationColumns = wantRelation;
  const countLabel =
    recordKind === "entity"
      ? t("model.record.entity.modelNames")
      : recordKind === "relation"
        ? t("model.record.relation.modelNames")
        : t("feature.dataImportProjects.recordUpdateOverride.records");

  if (rows.length === 0) {
    return (
      <p className="rounded-[var(--radius-md)] border border-border-default p-4 text-12-regular text-fg-3">
        {t("feature.common.noData")}
      </p>
    );
  }

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-12-regular text-fg-3">
          {totalCount} {countLabel}
        </p>
        <CopyIdsButton ids={rows.map(({ record }) => record.id)} />
      </div>
      <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default">
        <table className="w-full min-w-[880px] border-collapse text-12-regular">
          <thead className="bg-bg-subtle text-fg-2">
            <tr>
              <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
                {t("model.common.name")}
              </th>
              <th className="border-b border-border-default px-3 py-2 text-left font-semibold">{t("ui.date")}</th>
              <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
                {t("model.category.modelNames")}
              </th>
              {showRelationColumns ? (
                <>
                  <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
                    {t("model.record.relation.source")}
                  </th>
                  <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
                    {t("model.record.relation.target")}
                  </th>
                </>
              ) : null}
              <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
                {t("model.common.updatedAt")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ kind, record }) => {
              const relation = kind === "relation" ? record : null;
              return (
                <tr key={record.id} className="border-t border-border-default align-top first:border-t-0">
                  <td className="px-3 py-2">
                    <Link
                      className="inline-flex items-center gap-1 text-primary-700 hover:underline"
                      to={recordPath(record, kind)}
                    >
                      {getDisplayName(record) || record.nameJa || record.id}
                      <ExternalLink className="size-3" />
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-fg-2">
                    {record.date}
                    {record.endDate ? ` ~ ${record.endDate}` : ""}
                  </td>
                  <td className="px-3 py-2">
                    <RecordCategories record={record} />
                  </td>
                  {showRelationColumns ? (
                    <>
                      <td className="px-3 py-2 text-fg-2">
                        {relation?.relationRecord?.sourceRecordEdges
                          .map((edge) => (edge.entity ? getDisplayName(edge.entity) : ""))
                          .filter(Boolean)
                          .join(", ") || "-"}
                      </td>
                      <td className="px-3 py-2 text-fg-2">
                        {relation?.relationRecord?.targetRecordEdges
                          .map((edge) => (edge.entity ? getDisplayName(edge.entity) : ""))
                          .filter(Boolean)
                          .join(", ") || "-"}
                      </td>
                    </>
                  ) : null}
                  <td className="whitespace-nowrap px-3 py-2 text-fg-2">
                    {t("date.long", { date: parseISO(record.updatedAt) })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
